import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { SelectField, TextField, PasswordField } from '../shared/FormField';
import { useLocationOptions } from '../shared/useLocationOptions';
import { resolveUserScope } from '../shared/userScope';

const parseError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Unable to update user.';

export default function EditUserModal({
  isOpen,
  onClose,
  onSave,
  row,
  institutions = [],
  existingRows = [],
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    institution_uuid: '',
    state: '',
    lga: '',
    is_active: 'true',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state);
  const { isNationalScope, requiresState, requiresLga } = resolveUserScope(row ?? {});
  const isStateOfficer = requiresState && !requiresLga;
  const visibleStateOptions = isNationalScope ? [] : stateOptions;
  const visibleLgaOptions = isNationalScope || isStateOfficer ? [] : lgaOptions;

  const set = (k) => (v) =>
    setForm((f) => {
      if (k === 'state') {
        return { ...f, state: v, lga: '' };
      }
      return { ...f, [k]: v };
    });

  useEffect(() => {
    if (!row) return;

    const resolvedScope = resolveUserScope(row);
    setForm({
      name: row.name ?? '',
      email: row.email ?? '',
      password: '',
      institution_uuid: row.institution_uuid ?? row.institution_id ?? '',
      state: resolvedScope.isNationalScope ? '' : (row.state ?? ''),
      lga: resolvedScope.isNationalScope || !resolvedScope.requiresLga ? '' : (row.lga ?? ''),
      is_active: row.is_active === false ? 'false' : 'true',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const institutionOptions = institutions.map((i) => ({ value: i.uuid ?? i.id, label: i.name }));

  const submit = async () => {
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.institution_uuid) return;

    if (requiresState && !form.state.trim()) {
      setErrMsg('State is required for State Officer (R04) and Field Officer (R05) users.');
      setStatus('error');
      return;
    }

    if (requiresLga && !form.lga.trim()) {
      setErrMsg('LGA is required for Field Officer (R05) users.');
      setStatus('error');
      return;
    }

    const duplicate = existingRows.some(
      (candidate) =>
        (candidate.uuid ?? candidate.id) !== (row?.uuid ?? row?.id) &&
        candidate.email?.toLowerCase() === form.email.trim().toLowerCase()
    );

    if (duplicate) {
      setErrMsg('A user with this email already exists in the system.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');
    try {
      const inst = institutions.find((i) => (i.uuid ?? i.id) === form.institution_uuid);
      await onSave({
        ...row,
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password.trim(),
        institution_uuid: form.institution_uuid,
        institution_id: form.institution_uuid,
        institution_label: inst?.name || form.institution_uuid,
        state: isNationalScope ? null : (form.state.trim() || null),
        // A State Officer covers the whole state — an LGA would narrow them to one LGA.
        lga: isNationalScope || isStateOfficer ? null : (form.lga.trim() || null),
        is_active: form.is_active === 'true',
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseError(error));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    onClose();
  };

  return (
    <Modal
      title="Edit User"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="User Updated"
      successMessage="The user changes were saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Full Name"
        required
        value={form.name}
        onChange={set('name')}
        placeholder="Enter full name"
      />
      <TextField
        label="Email"
        required
        type="email"
        value={form.email}
        onChange={set('email')}
        placeholder="Enter email"
      />
      <PasswordField
        label="Password"
        value={form.password}
        onChange={set('password')}
        placeholder="Leave blank to keep the current password"
        note="The current password can't be retrieved or displayed for security reasons. Type a value here only if you want to set a new one — otherwise leave this blank and the existing password stays unchanged."
      />
      <SelectField
        label="Institution"
        required
        value={form.institution_uuid}
        onChange={set('institution_uuid')}
        options={institutionOptions}
      />
      <SelectField
        label="State"
        value={form.state}
        onChange={set('state')}
        options={visibleStateOptions}
        emptyOptionLabel={isNationalScope ? 'All States' : 'Select State'}
        emptyOptionDisabled={!isNationalScope}
        required={requiresState}
        note={isNationalScope
          ? 'Leave this set to All States for national visibility.'
          : (isStateOfficer
            ? 'The one state this State Officer works in — they see every LGA and ward in it.'
            : 'The state of this Field Officer’s LGA.')}
      />
      <SelectField
        label="LGA"
        value={form.lga}
        onChange={set('lga')}
        options={visibleLgaOptions}
        emptyOptionLabel={isNationalScope ? 'All LGAs' : undefined}
        emptyOptionDisabled={!isNationalScope}
        placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select State first'}
        disabled={isStateOfficer || !form.state || loadingLgas}
        required={requiresLga}
        note={requiresLga
          ? 'The LGA this Field Officer works in — they see every ward in it.'
          : isStateOfficer
            ? 'Not used — a State Officer covers every LGA in their state.'
          : (isNationalScope
            ? 'National users can leave this blank.'
            : (form.state ? 'Optional for national users.' : 'Select a state first if you want to choose an LGA.'))}
      />
      <SelectField
        label="Status"
        value={form.is_active}
        onChange={set('is_active')}
        options={[
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ]}
      />
    </Modal>
  );
}
